/**
 * The card primitives come from the format choice cards and the shared step text styles, so a
 * single-choice group and a multi-choice group read the same. The tiles and gallery variants add
 * small and picture cards on the same tokens.
 */
import styled, { css } from "styled-components";
import { borderRadius, borderWidth, spacing, typographyMixin } from "@magicbi/ui-library";
import {
  Card as BaseCard,
  Cards as BaseCards,
  Check as BaseCheck,
  Legend as BaseLegend,
} from "../DistributionFormatChoice/DistributionFormatChoice.styles";

export {
  Card,
  CardText,
  Check,
  Fieldset,
  Required,
} from "../DistributionFormatChoice/DistributionFormatChoice.styles";
export { ErrorText, Hint } from "../DistributionStepSection/DistributionStepSection.styles";

/** The legend; with `$hidden` it stays for screen readers only, as the group's section draws the heading. */
export const Legend = styled(BaseLegend)<{ $hidden?: boolean }>`
  ${({ $hidden }) =>
    $hidden &&
    css`
      position: absolute;
      width: 1px;
      height: 1px;
      margin: 0;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
    `}
`;

/** The cards; outlined in the error colour while the group shows an error. */
export const Cards = styled(BaseCards)<{ $invalid: boolean; $columns?: number; $plain?: boolean }>`
  ${({ $plain, theme }) =>
    $plain &&
    css`
      gap: ${spacing[3]};
      > label {
        gap: ${spacing[2.5]};
        padding: ${spacing[3.5]};
        border-radius: ${borderRadius[10]};
        background: ${theme.colors.background.primary};
        &:hover {
          background: ${theme.colors.background.primary};
        }
      }
      /* The chosen card: a 2px primary outline, the padding giving back the extra 1px. */
      > label:has(input:checked) {
        padding: calc(${spacing[3.5]} - 1px);
        border: ${borderWidth[2]} solid ${theme.colors.interactive.primary};
        background: ${theme.colors.background.primary};
      }
      > label > span > span {
        ${typographyMixin("caption", 3)}
      }
    `}
  ${({ $columns, theme }) =>
    $columns &&
    css`
      grid-template-columns: repeat(${$columns}, minmax(0, 1fr));
      ${theme.breakpoints.mediaQueries.down.md} {
        grid-template-columns: minmax(0, 1fr);
      }
    `}
  ${({ $invalid, theme }) =>
    $invalid &&
    css`
      > label {
        border-color: ${theme.colors.error[500]};
      }
    `}
`;

/** An option's icon: a round tinted badge at the card's end (prototype `.n1-ci`). */
export const CardIcon = styled.span`
  display: grid;
  place-items: center;
  flex: none;
  width: 2rem;
  height: 2rem;
  margin-left: auto;
  border-radius: ${borderRadius.full};
  background: ${({ theme }) => theme.colors.background.tertiary};
  color: ${({ theme }) => theme.colors.text.interactive};

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

export const LegendDetail = styled.span`
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/* ─── Gallery ─────────────────────────────────────────────────────────────── */

/** The picture cards; outlined in the error colour while the group shows an error, as the list's cards are. */
export const Gallery = styled.div<{ $invalid: boolean }>`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(12.25rem, 1fr));
  gap: ${spacing[3]};
  ${({ $invalid, theme }) =>
    $invalid &&
    css`
      > div,
      > div:hover {
        border-color: ${theme.colors.error[500]};
      }
    `}
`;

export const GalleryCard = styled.div<{ $checked: boolean; $disabled: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  border: 1px solid
    ${({ theme, $checked }) => ($checked ? theme.colors.interactive.primary : theme.colors.border.subtle)};
  border-radius: ${borderRadius.xl};
  background: ${({ theme }) => theme.colors.background.primary};
  box-shadow: ${({ theme, $checked }) => ($checked ? `0 0 0 1px ${theme.colors.interactive.primary}` : "none")};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  &:hover {
    border-color: ${({ theme, $checked }) => ($checked ? theme.colors.interactive.primary : theme.colors.border.default)};
  }
  &:has(input:focus-visible) {
    outline: ${borderWidth[2]} solid ${({ theme }) => theme.colors.interactive.primary};
    outline-offset: ${spacing[0.5]};
  }
`;

export const Pick = styled.label<{ $disabled: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
`;

export const Media = styled.span`
  position: relative;
  display: block;
  min-width: 0;
  padding: ${spacing[2]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.subtle};
  background: ${({ theme }) => theme.colors.background.secondary};
`;

const THUMB_COLUMNS = { row: 3, pair: 2, page: 1, full: 1 } as const;

export const Thumbs = styled.span<{ $fit: keyof typeof THUMB_COLUMNS }>`
  display: grid;
  grid-template-columns: ${({ $fit }) => `repeat(${THUMB_COLUMNS[$fit]}, minmax(0, 1fr))`};
  gap: ${spacing[2]};
  align-items: center;
  & > span {
    display: block;
    min-width: 0;
    ${({ $fit }) => ($fit === "page" ? "width: 52%; justify-self: center;" : "")}
  }
`;

export const More = styled.span`
  position: absolute;
  right: ${spacing[3]};
  bottom: ${spacing[3]};
  padding: 0 ${spacing[2]};
  border-radius: ${borderRadius.full};
  background: ${({ theme }) => theme.colors.background.inverse};
  color: ${({ theme }) => theme.colors.text.inverse};
  ${typographyMixin("caption", 2)}
`;

export const Body = styled.span`
  display: grid;
  gap: ${spacing[1]};
  min-width: 0;
  padding: ${spacing[2]} ${spacing[3]} 0;
`;

export const TitleRow = styled.span`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[2]};
  min-width: 0;
  ${typographyMixin("body", 2)}
  color: ${({ theme }) => theme.colors.text.primary};
  & > input {
    flex: none;
    width: 1rem;
    height: 1rem;
    margin: ${spacing[0.5]} 0 0;
    accent-color: ${({ theme }) => theme.colors.interactive.primary};
  }
  & > input:focus-visible {
    outline: none;
  }
  & > b {
    min-width: 0;
    overflow-wrap: anywhere;
    font-weight: 600;
  }
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  flex: none;
  margin-left: auto;
  padding: 0 ${spacing[2]};
  border-radius: ${borderRadius.full};
  background: ${({ theme }) => theme.colors.primary[100]};
  color: ${({ theme }) => theme.colors.text.link};
  ${typographyMixin("caption", 2)}
`;

export const Description = styled.span`
  display: block;
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Meta = styled.span`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  min-height: 1.25rem;
`;

export const Swatches = styled.span`
  display: inline-flex;
  gap: ${spacing[1]};
`;

export const Swatch = styled.span`
  width: 1rem;
  height: 1rem;
  border-radius: ${borderRadius.sm};
  box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.border.default};
`;

export const Tag = styled.span<{ $tone: "neutral" | "accent" }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  padding: 0 ${spacing[2]};
  border-radius: ${borderRadius.full};
  background: ${({ theme, $tone }) => ($tone === "accent" ? theme.colors.primary[100] : theme.colors.background.tertiary)};
  color: ${({ theme, $tone }) => ($tone === "accent" ? theme.colors.text.link : theme.colors.text.secondary)};
  ${typographyMixin("caption", 2)}
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  margin-top: auto;
  padding: ${spacing[2]} ${spacing[3]} ${spacing[3]};
`;

export const ActionButton = styled.button`
  display: inline-flex;
  flex: 1 1 0;
  align-items: center;
  justify-content: center;
  gap: ${spacing[1]};
  min-width: 0;
  height: 2rem;
  padding: 0 ${spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${borderRadius.lg};
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  ${typographyMixin("caption", 2)}
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }
  &:focus-visible {
    outline: ${borderWidth[2]} solid ${({ theme }) => theme.colors.interactive.primary};
    outline-offset: ${spacing[0.5]};
  }
`;

export const AddTile = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  min-height: 12rem;
  padding: ${spacing[5]} ${spacing[4]};
  border: 1px dashed ${({ theme }) => theme.colors.border.strong};
  border-radius: ${borderRadius.xl};
  background: ${({ theme }) => theme.colors.background.secondary};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
  cursor: pointer;
  & > b {
    ${typographyMixin("body", 2)}
  }
  & > span:last-child {
    ${typographyMixin("caption", 4)}
    color: ${({ theme }) => theme.colors.text.secondary};
  }
  &:hover {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }
  &:focus-visible {
    outline: ${borderWidth[2]} solid ${({ theme }) => theme.colors.interactive.primary};
    outline-offset: ${spacing[0.5]};
  }
`;

export const AddIcon = styled.span`
  display: grid;
  place-items: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${borderRadius.full};
  background: ${({ theme }) => theme.colors.primary[100]};
  color: ${({ theme }) => theme.colors.text.link};
`;

/* ─── Tiles ───────────────────────────────────────────────────────────────── */

/** Tiles: small cards in a wrapping row, the visual on top, the radio kept for keyboards and readers. */
export const TileCards = styled(Cards)`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
`;

export const TileCard = styled(BaseCard)`
  position: relative;
  flex-direction: column;
  gap: ${spacing[1]};
  width: 8.5rem;
  padding: ${spacing[2]};
`;

export const TileCheck = styled(BaseCheck)`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: 0;
  opacity: 0;
`;

/** An option's visual: between the title and the detail in a list, on top in a tile. */
export const Visual = styled.span`
  display: block;
  min-width: 0;
`;
